import type { NextPageContext } from 'next';

import { Item, SeoData } from 'types';
import {
  getCategoryFromSlug,
  getCollection,
  getItemList,
  getMarketingCopy,
  getPageSEO,
  getPublishedPage,
  UnpackedItemList,
} from '@root/server/services/Collection';
import productCategories, { CategoryDto } from 'components/Collection/pages/categories';
import CollectionCategoryPageComponent, { MarketingCopy } from 'components/Collection/pages/category';
import { withPageStaticProps } from 'utils/header';
import { CollectionPageContents } from '@root/sanity/sanity.types';

type CollectionCategoryPageProps = {
  activeCategory: CategoryDto;
  marketingCopy?: MarketingCopy;
  products: Item[];
};

/**
 * @note We pass a `key` prop into the page component in order to remount, because currently there's
 *  (seemingly) no other (good) way of syncing the parser when the active category changes. This is a
 *  minor point, but may be something we can improve later on.
 */
export const CollectionCategoryPage = ({ activeCategory, products, marketingCopy }: CollectionCategoryPageProps) => (
  <CollectionCategoryPageComponent
    key={activeCategory.slug!}
    activeCategory={activeCategory}
    products={products}
    marketingCopy={marketingCopy}
  />
);

// This function gets called at build time
export const getStaticPaths = async () => {
  const categories = productCategories.filter((c) => c.slug !== null);

  const paths = categories.map((c) => ({
    params: {
      category: c.slug,
    },
  }));

  // We'll pre-render only these paths at build time.
  // { fallback: false } means other routes should 404.
  return { paths, fallback: false };
};

type Context = NextPageContext & {
  params: {
    category: string;
  };
};

export const getStaticProps = withPageStaticProps(async ({ params }: Context) => {
  const { category } = params;
  const productCategory = getCategoryFromSlug(category);
  const collectionSlug = category === 'tuxedos-and-suits' ? 'collection' : `collection/${category}`;

  const sanityPage = await getPublishedPage(collectionSlug);

  let seo: SeoData | null = null;
  let marketingCopy: CollectionPageContents | null = null;
  let itemList: UnpackedItemList = [];

  if (sanityPage) {
    seo = getPageSEO(sanityPage);
    marketingCopy = getMarketingCopy(sanityPage);
    itemList = await getItemList(sanityPage);
  }

  const products = await getCollection(productCategory!.name, itemList);

  return {
    props: {
      activeCategory: {
        displayName: productCategory!.displayName,
        name: productCategory!.name,
        slug: category,
      },
      seo,
      products,
      marketingCopy,
    },
  };
});

export default CollectionCategoryPage;
